import { useState, useRef, useCallback, useEffect } from "react";
import axios from "axios";
import { jsPDF } from "jspdf";
import Disclaimer from "../components/Disclaimer";
import LoadingSpinner from "../components/LoadingSpinner";
import { useLanguage } from "../LanguageContext";

const API = process.env.REACT_APP_API_URL || "http://localhost:8001";

function RiskBadge({ level }) {
  const cfg = {
    low:      { cls: "bg-emerald-50 border-emerald-200 text-emerald-700", dot: "bg-emerald-500" },
    moderate: { cls: "bg-amber-50 border-amber-200 text-amber-700",       dot: "bg-amber-500"   },
    high:     { cls: "bg-rose-50 border-rose-200 text-rose-700",          dot: "bg-rose-500"    },
  };
  const { cls, dot } = cfg[level?.toLowerCase()] || cfg.low;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold border ${cls}`}>
      <span className={`w-2 h-2 rounded-full ${dot}`} />
      {level ? level.charAt(0).toUpperCase() + level.slice(1) : "Unknown"} Risk
    </span>
  );
}

function ImageDropZone({ preview, onFile, disabled, t }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef();

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f && f.type.startsWith("image/")) onFile(f);
  }, [onFile]);

  if (preview) {
    return (
      <div className="relative rounded-2xl overflow-hidden border border-stone-200">
        <img src={preview} alt="Uploaded skin" className="w-full max-h-72 object-cover" />
        <button
          onClick={() => !disabled && onFile(null)}
          className="absolute top-3 right-3 w-8 h-8 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors"
          title={t.removeImage}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => !disabled && inputRef.current?.click()}
      className={`flex flex-col items-center justify-center gap-3 border-2 border-dashed rounded-2xl p-10 cursor-pointer transition-all ${
        dragging ? "border-amber-400 bg-amber-50" : "border-stone-200 bg-stone-50 hover:border-amber-300 hover:bg-amber-50"
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      <div className="w-14 h-14 rounded-2xl bg-white border border-stone-200 shadow-sm flex items-center justify-center">
        <svg className="w-7 h-7 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </div>
      <div className="text-center">
        <p className="text-sm font-medium text-stone-700">
          {dragging ? t.dropHere : t.dragDrop}
        </p>
        <p className="text-xs text-stone-400 mt-1">{t.fileTypes}</p>
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden"
        onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} />
    </div>
  );
}

function FollowUpForm({ questions, onSubmit, loading, t }) {
  const [answers, setAnswers] = useState({});
  const allAnswered = questions.every((q) => answers[q]?.trim());

  return (
    <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6">
      <div className="flex items-center gap-2 mb-5">
        <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center">
          <svg className="w-4 h-4 text-amber-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="font-semibold text-stone-800">{t.followUpTitle}</h3>
        <p className="text-xs text-stone-400 ml-auto">{t.followUpSub}</p>
      </div>
      <div className="space-y-4">
        {questions.map((q) => (
          <div key={q}>
            <label className="block text-sm font-medium text-stone-700 mb-1.5">{q}</label>
            <input
              type="text"
              placeholder={t.followUpPlaceholder}
              value={answers[q] || ""}
              onChange={(e) => setAnswers((a) => ({ ...a, [q]: e.target.value }))}
              className="w-full px-3 py-2.5 text-sm border border-stone-200 rounded-lg outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition"
            />
          </div>
        ))}
      </div>
      <button
        disabled={!allAnswered || loading}
        onClick={() => onSubmit(answers)}
        className="mt-5 w-full py-2.5 bg-amber-700 hover:bg-amber-800 disabled:opacity-40 text-white text-sm font-semibold rounded-xl transition-colors"
      >
        {loading ? t.refining : t.getRefined}
      </button>
    </div>
  );
}

export default function SkinAnalyzer() {
  const { t, lang } = useLanguage();
  const s = t.skin;
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [followupLoading, setFollowupLoading] = useState(false);
  const [refinedResult, setRefinedResult] = useState(null);
  const [error, setError] = useState(null);

  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (showCamera && videoRef.current && cameraStream) {
      videoRef.current.srcObject = cameraStream;
      videoRef.current.play().catch(() => {});
    }
  }, [showCamera, cameraStream]);

  const openCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      setCameraStream(stream);
      setShowCamera(true);
    } catch {
      alert("Camera access denied or not available on this device.");
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      const f = new File([blob], "camera-capture.jpg", { type: "image/jpeg" });
      handleFileChange(f);
      closeCamera();
    }, "image/jpeg", 0.92);
  };

  const closeCamera = () => {
    cameraStream?.getTracks().forEach(t => t.stop());
    setCameraStream(null);
    setShowCamera(false);
  };

  const downloadPDF = () => {
    const doc = new jsPDF();
    const addText = (text, x, startY, maxW, lh) => {
      const lines = doc.splitTextToSize(String(text), maxW);
      let y = startY;
      for (const line of lines) {
        if (y > 275) { doc.addPage(); y = 20; }
        doc.text(line, x, y);
        y += lh;
      }
      return y;
    };
    doc.setFillColor(180, 83, 9);
    doc.rect(0, 0, 210, 22, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(15);
    doc.setFont("helvetica", "bold");
    doc.text("WorryLens \u2014 Skin Analysis Report", 14, 14);
    doc.setTextColor(40, 40, 40);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    let y = 32;
    doc.text(`Date: ${new Date().toLocaleString()}`, 14, y); y += 6;
    doc.text(`File: ${file?.name || "image"}`, 14, y); y += 10;
    doc.setFont("helvetica", "bold"); doc.setFontSize(11);
    doc.text("Risk Assessment", 14, y); y += 6;
    doc.setFont("helvetica", "normal"); doc.setFontSize(9);
    const riskLabel = result.risk_level ? result.risk_level.charAt(0).toUpperCase() + result.risk_level.slice(1) : "Unknown";
    doc.text(`Risk Level: ${riskLabel}`, 14, y); y += 6;
    doc.text(`Confidence: ${result.confidence || "\u2014"}`, 14, y); y += 6;
    if (result.risk_explanation) { y = addText(result.risk_explanation, 14, y, 182, 5); }
    y += 6;
    if (result.conditions?.length) {
      doc.setFont("helvetica", "bold"); doc.setFontSize(11);
      doc.text("Possible Conditions", 14, y); y += 6;
      doc.setFont("helvetica", "normal"); doc.setFontSize(9);
      for (const c of result.conditions) { doc.text(`\u2022 ${c}`, 14, y); y += 6; }
      y += 4;
    }
    if (result.observations) {
      doc.setFont("helvetica", "bold"); doc.setFontSize(11);
      doc.text("Observations", 14, y); y += 6;
      doc.setFont("helvetica", "normal"); doc.setFontSize(9);
      y = addText(result.observations, 14, y, 182, 5); y += 6;
    }
    if (result.recommendations?.length) {
      doc.setFont("helvetica", "bold"); doc.setFontSize(11);
      doc.text("Recommendations", 14, y); y += 6;
      doc.setFont("helvetica", "normal"); doc.setFontSize(9);
      result.recommendations.forEach((r, i) => { y = addText(`${i + 1}. ${r}`, 14, y, 182, 5); });
      y += 6;
    }
    if (refinedResult) {
      if (y > 230) { doc.addPage(); y = 20; }
      doc.setFont("helvetica", "bold"); doc.setFontSize(11);
      doc.text("Refined Assessment", 14, y); y += 6;
      doc.setFont("helvetica", "normal"); doc.setFontSize(9);
      const rl = refinedResult.risk_level ? refinedResult.risk_level.charAt(0).toUpperCase() + refinedResult.risk_level.slice(1) : "Unknown";
      doc.text(`Risk Level: ${rl}`, 14, y); y += 6;
      if (refinedResult.urgency) { doc.text(`Urgency: ${refinedResult.urgency}`, 14, y); y += 6; }
      if (refinedResult.refined_explanation) { y = addText(refinedResult.refined_explanation, 14, y, 182, 5); y += 4; }
      if (refinedResult.action_plan?.length) {
        refinedResult.action_plan.forEach((a, i) => { y = addText(`${i + 1}. ${a}`, 14, y, 182, 5); });
      }
      y += 6;
    }
    doc.setTextColor(120, 120, 120); doc.setFontSize(8);
    addText("This is a health screening tool only \u2014 not a medical diagnosis. Please consult a dermatologist for proper evaluation.", 14, y, 182, 4.5);
    doc.save(`worrylens-skin-${Date.now()}.pdf`);
  };

  const saveToHistory = (data, filename, isRefined = false) => {
    const history = JSON.parse(localStorage.getItem("wl_history") || "[]");
    history.unshift({
      id: Date.now(), type: "skin", filename,
      date: new Date().toISOString(),
      result: isRefined ? "Refined: " + (data.refined_conditions || []).join(", ") : (data.conditions || []).join(", "),
      risk_level: data.risk_level,
      observations: data.observations || data.refined_explanation,
      recommendations: data.recommendations || data.action_plan,
    });
    localStorage.setItem("wl_history", JSON.stringify(history.slice(0, 20)));
  };

  const handleFileChange = (f) => {
    if (!f) { setFile(null); setPreview(null); setResult(null); setRefinedResult(null); setError(null); return; }
    setFile(f); setResult(null); setRefinedResult(null); setError(null);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(f);
  };

  const analyze = async () => {
    if (!file) return;
    setLoading(true); setError(null);

    let uploadBlob = file;
    try {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = reject; img.src = objectUrl; });
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext("2d").drawImage(img, 0, 0);
      URL.revokeObjectURL(objectUrl);
      uploadBlob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
    } catch { /* fall back to original file */ }

    const form = new FormData();
    form.append("file", uploadBlob, "image.jpg");
    form.append("language", lang);
    try {
      const { data } = await axios.post(`${API}/analyze-skin`, form);
      setResult(data);
      saveToHistory(data, file.name);
    } catch (err) {
      setError(err.response?.data?.detail || s.errorFallback);
    } finally { setLoading(false); }
  };

  const handleFollowUp = async (answers) => {
    setFollowupLoading(true);
    try {
      const { data } = await axios.post(`${API}/skin-followup`, { original_analysis: result, answers, language: lang });
      setRefinedResult(data);
      saveToHistory(data, file?.name || "image", true);
    } catch { setError(s.followUpFailed); }
    finally { setFollowupLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 animate-fade-in">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-amber-700 flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-stone-900">{s.title}</h1>
            <p className="text-sm text-stone-500">{s.subtitle}</p>
          </div>
        </div>
      </div>

      <Disclaimer className="mb-6" />

      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6 mb-6">
        <h2 className="font-semibold text-stone-800 mb-4">{s.uploadTitle}</h2>
        <ImageDropZone preview={preview} onFile={handleFileChange} disabled={loading} t={s} />
        {!preview && (
          <button onClick={openCamera} disabled={loading}
            className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 border border-stone-200 hover:border-amber-300 hover:bg-amber-50 text-stone-600 hover:text-amber-800 text-sm font-medium rounded-xl transition-colors disabled:opacity-40">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Take Photo with Camera
          </button>
        )}
        {file && !loading && (
          <button onClick={analyze}
            className="mt-4 w-full py-3 bg-amber-700 hover:bg-amber-800 text-white font-semibold rounded-xl transition-colors">
            {s.analyzeImage}
          </button>
        )}
      </div>

      {loading && (
        <div className="bg-white rounded-2xl border border-stone-200 shadow-sm">
          <LoadingSpinner message={s.loading} />
        </div>
      )}

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 text-sm text-rose-700">
          <strong>{s.error}:</strong> {error}
        </div>
      )}

      {result && !loading && (
        <div className="space-y-5 animate-slide-up">
          <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6">
            <div className="flex items-start justify-between mb-5">
              <div>
                <p className="text-xs text-stone-500 uppercase tracking-wide mb-1">{s.riskAssessment}</p>
                <RiskBadge level={result.risk_level} />
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="text-right">
                  <p className="text-xs text-stone-500 mb-1">{s.confidenceLabel}</p>
                  <span className={`text-sm font-semibold capitalize ${
                    result.confidence === "high" ? "text-emerald-600" :
                    result.confidence === "medium" ? "text-amber-600" : "text-rose-500"
                  }`}>{result.confidence}</span>
                </div>
                <button onClick={downloadPDF}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-stone-100 hover:bg-stone-200 text-stone-700 text-xs font-medium rounded-lg transition-colors">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download PDF
                </button>
              </div>
            </div>
            {result.risk_explanation && (
              <p className="text-sm text-stone-600 mb-4 leading-relaxed">{result.risk_explanation}</p>
            )}
            {result.conditions && result.conditions.length > 0 && (
              <div>
                <p className="text-xs text-stone-500 mb-2 uppercase tracking-wide">{s.possibleConditions}</p>
                <div className="flex flex-wrap gap-2">
                  {result.conditions.map((c) => (
                    <span key={c} className="text-xs bg-amber-50 text-amber-800 border border-amber-200 px-2.5 py-1 rounded-full font-medium">
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {result.observations && (
            <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-full bg-amber-700 flex items-center justify-center">
                  <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-stone-800">{s.aiObservations}</h3>
                <span className="text-xs text-stone-400 ml-auto">{s.poweredByVision}</span>
              </div>
              <p className="text-sm text-stone-700 leading-relaxed">{result.observations}</p>
            </div>
          )}

          {result.recommendations && result.recommendations.length > 0 && (
            <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6">
              <h3 className="font-semibold text-stone-800 mb-4">{s.recommendations}</h3>
              <ul className="space-y-2">
                {result.recommendations.map((r, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-stone-700">
                    <div className="w-5 h-5 rounded-full bg-amber-100 text-amber-800 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                      {i + 1}
                    </div>
                    {r}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.follow_up_questions && result.follow_up_questions.length > 0 && !refinedResult && (
            <FollowUpForm questions={result.follow_up_questions} onSubmit={handleFollowUp} loading={followupLoading} t={s} />
          )}

          {followupLoading && (
            <div className="bg-white rounded-2xl border border-stone-200 shadow-sm">
              <LoadingSpinner message={s.refining} />
            </div>
          )}

          {refinedResult && !followupLoading && (
            <div className="bg-gradient-to-br from-amber-50 to-stone-50 border border-amber-200 rounded-2xl p-6 animate-slide-up">
              <div className="flex items-center gap-2 mb-4">
                <svg className="w-5 h-5 text-amber-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
                <h3 className="font-semibold text-stone-900">{s.refinedAssessment}</h3>
                <RiskBadge level={refinedResult.risk_level} />
              </div>

              {refinedResult.refined_explanation && (
                <p className="text-sm text-stone-700 leading-relaxed mb-4">{refinedResult.refined_explanation}</p>
              )}
              {refinedResult.refined_conditions && refinedResult.refined_conditions.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs text-stone-500 mb-2 uppercase tracking-wide">{s.refinedConditions}</p>
                  <div className="flex flex-wrap gap-2">
                    {refinedResult.refined_conditions.map((c) => (
                      <span key={c} className="text-xs bg-white text-amber-800 border border-amber-200 px-2.5 py-1 rounded-full font-medium">
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {refinedResult.action_plan && refinedResult.action_plan.length > 0 && (
                <div>
                  <p className="text-xs text-stone-500 mb-2 uppercase tracking-wide">{s.actionPlan}</p>
                  <ul className="space-y-2">
                    {refinedResult.action_plan.map((a, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-stone-700">
                        <div className="w-5 h-5 rounded-full bg-amber-200 text-amber-900 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                          {i + 1}
                        </div>
                        {a}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {refinedResult.urgency && (
                <div className="mt-4 pt-4 border-t border-amber-200 flex items-center gap-2 text-sm">
                  <span className="text-stone-500">{s.urgency}</span>
                  <span className={`font-semibold capitalize ${
                    refinedResult.urgency === "urgent" ? "text-rose-600" :
                    refinedResult.urgency === "soon" ? "text-amber-700" : "text-emerald-600"
                  }`}>{refinedResult.urgency}</span>
                </div>
              )}
            </div>
          )}

          <Disclaimer />
        </div>
      )}

      {showCamera && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl overflow-hidden w-full max-w-lg">
            <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100">
              <h3 className="font-semibold text-stone-900">Take a Photo</h3>
              <button onClick={closeCamera} className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-500">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <video ref={videoRef} autoPlay playsInline muted className="w-full bg-black" style={{ maxHeight: "60vh" }} />
            <canvas ref={canvasRef} className="hidden" />
            <div className="p-4 flex gap-3">
              <button onClick={capturePhoto}
                className="flex-1 py-3 bg-amber-700 hover:bg-amber-800 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Capture Photo
              </button>
              <button onClick={closeCamera}
                className="px-5 py-3 border border-stone-200 text-stone-700 font-medium rounded-xl hover:bg-stone-50 transition-colors">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
